import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import StepDialog from '../components/StepDialog';
import ReadinessBadge from '../components/ReadinessBadge';
import {
  MessageSquare, AlertTriangle, Users, Building, Zap, Send,
  Check, X, Edit, Clock, Shield, FileText, ChevronRight,
  Loader2, RefreshCw, Eye, Mail, Phone, Radio, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useAuth } from '../context/AuthContext';
import AISuggestionsBanner from '../components/AISuggestionsBanner';
import { persistAcceptedSuggestions, suggestionName } from '../utils/acceptSuggestions';

const SCENARIO_ICONS = {
  cyber_attack: Shield,
  facility_loss: Building,
  power_failure: Zap,
  supply_chain: Users,
  pandemic: Users,
  natural_disaster: AlertTriangle,
  data_breach: Shield,
  it_outage: Zap
};

const AUDIENCE_ICONS = {
  staff: Users,
  customers: Globe,
  vendors: Building,
  media: Radio,
  regulators: FileText
};

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-sky-100 text-sky-700',
  archived: 'bg-slate-50 text-slate-500'
};

export default function CrisisCommunicationPage() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState(null);
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [commTests, setCommTests] = useState([]);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testStep, setTestStep] = useState(0);
  const [draftStep, setDraftStep] = useState(0);
  
  // Dialog states
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // Communication Test form state
  const [testForm, setTestForm] = useState({
    test_type: 'call_tree',
    participants_contacted: 0,
    participants_responded: 0,
    average_response_time: '',
    issues_identified: '',
    notes: '',
  });
  
  // Draft form state
  const [draftForm, setDraftForm] = useState({
    scenario: '',
    audience: '',
    custom_variables: {},
    notes: ''
  });
  const [draftPreview, setDraftPreview] = useState(null);
  
  // Approval state
  const [approvalAction, setApprovalAction] = useState('approve');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [editedContent, setEditedContent] = useState('');

  const isAdmin = user?.role === 'bcm_manager' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const [templatesRes, messagesRes, testsRes] = await Promise.all([
        api.get('/bia-engine/crisis/templates'),
        api.get('/bia-engine/crisis/messages'),
        api.get('/bia-engine/crisis/communication-tests')
      ]);
      
      setTemplates(templatesRes.data);
      setMessages(messagesRes.data.messages);
      setCommTests(testsRes.data?.tests || []);
    } catch (err) {
      toast.error('Failed to load crisis communication data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Accept AI crisis suggestions → create draft crisis messages (compose pattern):
  // AI subject + body land in the editable message. Dedupe by subject. Uses the
  // thin promote endpoint that writes to the collection this page displays.
  const handleAcceptCrisisSuggestions = async (items) => {
    const existingSubjects = (messages || []).map((m) => m.subject);
    const result = await persistAcceptedSuggestions({
      items,
      existingKeys: existingSubjects,
      keyOf: (raw) => {
        const o = raw && typeof raw === 'object' ? raw : {};
        return String(o.subject || suggestionName(raw));
      },
      createOne: async (raw) => {
        const o = raw && typeof raw === 'object' ? raw : {};
        // The narrative can arrive under many LLM-chosen keys; try the common ones,
        // then fall back to concatenating the suggestion's text fields so body is
        // NEVER empty (an empty body previously caused a 422 → "[object Object]").
        let body = o.body || o.content || o.message || o.template || o.statement ||
          o.holding_statement || o.narrative || o.draft || o.text || o.communication ||
          o.sample_message || '';
        if (!body && typeof o === 'object') {
          body = Object.entries(o)
            .filter(([k, v]) => typeof v === 'string' && v.trim() && !['id', 'label'].includes(k))
            .map(([, v]) => v)
            .join('\n\n');
        }
        if (!body) body = typeof raw === 'string' ? raw : '';
        // Prefer a descriptive subject; derive from a leading "Subject:" line in the
        // body before falling back to short codes like a bare regulation name.
        const subjectLine = (body.match(/^\s*subject\s*:\s*(.+)$/im) || [])[1];
        const subject = String(
          o.subject || o.name || o.title || o.template_name ||
          (subjectLine && subjectLine.trim()) || o.regulation || suggestionName(raw) || 'AI Crisis Draft'
        ).trim().slice(0, 300);
        if (!body) body = subject;
        const payload = { subject, body };
        if (o.audience) payload.audience = String(o.audience);
        if (o.channel) payload.channel = String(o.channel);
        if (o.tone || o.tone_notes) payload.tone_notes = String(o.tone || o.tone_notes);
        await api.post('/bia-engine/crisis/messages/promote', payload);
      },
    });
    await fetchData();
    return result;
  };

  const handleCreateDraft = async () => {
    if (!draftForm.scenario || !draftForm.audience) {
      toast.error('Please select scenario and audience');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post('/bia-engine/crisis/messages/draft', draftForm);
      setDraftPreview(res.data);
      toast.success('Draft created');
      await fetchData();
    } catch (err) {
      toast.error('Failed to create draft');
    } finally {
      setSaving(false);
    }
  };

  const handleApproval = async () => {
    if (!selectedMessage) return;
    
    setSaving(true);
    try {
      await api.post(`/bia-engine/crisis/messages/${selectedMessage.id}/approve`, {
        message_id: selectedMessage.id,
        action: approvalAction,
        edited_content: approvalAction === 'edit' ? editedContent : null,
        notes: approvalNotes
      });
      
      toast.success(`Message ${approvalAction}d`);
      setShowApprovalDialog(false);
      setSelectedMessage(null);
      await fetchData();
    } catch (err) {
      toast.error('Failed to process approval');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (messageId) => {
    if (!window.confirm('Send this crisis communication? This action will be logged.')) {
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/bia-engine/crisis/messages/${messageId}/send`);
      toast.success('Message sent successfully');
      await fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to send message'));
    } finally {
      setSaving(false);
    }
  };

  const openApprovalDialog = (message) => {
    setSelectedMessage(message);
    setApprovalAction('approve');
    setApprovalNotes('');
    setEditedContent(message.content);
    setShowApprovalDialog(true);
  };

  const openPreview = (message) => {
    setSelectedMessage(message);
    setShowPreviewDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="crisis-loading">
        <Loader2 className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  const pendingMessages = messages.filter(m => m.status === 'pending_approval' || m.status === 'draft');
  const approvedMessages = messages.filter(m => m.status === 'approved');
  const sentMessages = messages.filter(m => m.status === 'sent');

  return (
    <div className="space-y-6" data-testid="crisis-communication-page">
      <AISuggestionsBanner module="crisis_communication" onAccept={handleAcceptCrisisSuggestions}
        existingKeys={(messages || []).map((m) => m.subject || m.scenario_name)}
        itemKeyOf={(raw) => (raw && typeof raw === 'object' ? (raw.subject || raw.name || raw.title || suggestionName(raw)) : String(raw || ''))} />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-100 text-emerald-700">DO Phase</Badge>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">ISO 22301:7.4</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-red-600" />
            Crisis Communication
          </h1>
          <p className="text-slate-500 mt-1">Manage emergency communications with authority-gated approvals</p>
          <div className="mt-2">
            <ReadinessBadge
              total={messages.length}
              complete={sentMessages.length}
              label="Communications"
            />
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowDraftDialog(true)} data-testid="create-draft-btn">
            <Edit className="w-4 h-4 mr-2" />
            Draft New Message
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={pendingMessages.length > 0 ? 'border-amber-200' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Approval</p>
                <p className={`text-2xl font-bold ${pendingMessages.length > 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                  {pendingMessages.length}
                </p>
              </div>
              <Clock className={`w-8 h-8 ${pendingMessages.length > 0 ? 'text-amber-400' : 'text-slate-400'}`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ready to Send</p>
                <p className="text-2xl font-bold text-emerald-600">{approvedMessages.length}</p>
              </div>
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Sent</p>
                <p className="text-2xl font-bold text-sky-700">{sentMessages.length}</p>
              </div>
              <Send className="w-8 h-8 text-sky-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Messages</p>
                <p className="text-2xl font-bold text-slate-600">{messages.length}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="messages" className="space-y-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="messages" data-testid="messages-tab">
            <MessageSquare className="w-4 h-4 mr-2" />
            Messages
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">
            <FileText className="w-4 h-4 mr-2" />
            Template Library
          </TabsTrigger>
          <TabsTrigger value="tests" data-testid="comm-tests-tab">
            <Phone className="w-4 h-4 mr-2" />
            Communication Tests
          </TabsTrigger>
        </TabsList>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle>Crisis Messages</CardTitle>
              <CardDescription>
                All crisis communications with approval workflow
              </CardDescription>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">No crisis messages yet</p>
                  <p className="text-sm text-slate-400">Click &quot;Draft New Message&quot; to create one</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Scenario</TableHead>
                      <TableHead>Audience</TableHead>
                      <TableHead>Incident ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {messages.map(msg => {
                      const ScenarioIcon = SCENARIO_ICONS[msg.scenario] || AlertTriangle;
                      const AudienceIcon = AUDIENCE_ICONS[msg.audience] || Users;
                      
                      return (
                        <TableRow key={msg.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <ScenarioIcon className="w-4 h-4 text-slate-500" />
                              <span className="font-medium">{msg.scenario_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <AudienceIcon className="w-4 h-4 text-slate-400" />
                              {msg.audience?.replace('_', ' ').toUpperCase()}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{msg.incident_id}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[msg.status]}>
                              {msg.status?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="outline" onClick={() => openPreview(msg)}>
                                <Eye className="w-3 h-3" />
                              </Button>
                              {isAdmin && msg.status === 'draft' && (
                                <Button size="sm" variant="outline" onClick={() => openApprovalDialog(msg)}>
                                  <Check className="w-3 h-3" />
                                </Button>
                              )}
                              {isAdmin && msg.status === 'approved' && (
                                <Button size="sm" onClick={() => handleSend(msg.id)} disabled={saving}>
                                  <Send className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates?.scenarios?.map(scenario => {
              const Icon = SCENARIO_ICONS[scenario.value] || AlertTriangle;
              const scenarioTemplates = templates.templates[scenario.value]?.templates || {};
              
              return (
                <Card key={scenario.value} className="hover:border-sky-200 transition-all">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{scenario.name}</CardTitle>
                        <CardDescription className="text-xs">
                          {Object.keys(scenarioTemplates).length} audience templates
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 mb-3">Available audiences:</p>
                      {Object.keys(scenarioTemplates).map(audience => {
                        const AudienceIcon = AUDIENCE_ICONS[audience] || Users;
                        return (
                          <div key={audience} className="flex items-center gap-2 text-sm">
                            <AudienceIcon className="w-3 h-3 text-slate-400" />
                            <span>{audience.replace('_', ' ').toUpperCase()}</span>
                            {scenarioTemplates[audience]?.channel === 'sms_email' && (
                              <Badge variant="outline" className="text-xs ml-auto">SMS+Email</Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <Button 
                      className="w-full mt-4" 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setDraftForm(prev => ({ ...prev, scenario: scenario.value }));
                        setShowDraftDialog(true);
                      }}
                    >
                      Use Template
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Communication Tests Tab - ISO 7.4-7.5 */}
        <TabsContent value="tests">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Communication Tests
                    <Badge variant="outline" className="text-xs">ISO 7.4-7.5</Badge>
                  </CardTitle>
                  <CardDescription>
                    Log and track communication drills to verify contact procedures
                  </CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={() => setShowTestDialog(true)} className="bg-sky-700 hover:bg-sky-800">
                    <Phone className="w-4 h-4 mr-2" />
                    Log Test
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {commTests.length === 0 ? (
                <div className="text-center py-12">
                  <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No communication tests logged yet</p>
                  <p className="text-sm text-slate-400 mt-1">Test your call trees and notification systems regularly</p>
                  {isAdmin && (
                    <Button className="mt-4" onClick={() => setShowTestDialog(true)}>
                      Log First Test
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Test Type</TableHead>
                      <TableHead>Contacted</TableHead>
                      <TableHead>Responded</TableHead>
                      <TableHead>Response Rate</TableHead>
                      <TableHead>Avg Response Time</TableHead>
                      <TableHead>Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commTests.map((test, idx) => {
                      const responseRate = test.participants_contacted > 0 
                        ? Math.round((test.participants_responded / test.participants_contacted) * 100) 
                        : 0;
                      return (
                        <TableRow key={idx}>
                          <TableCell>{test.date}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{test.test_type.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell>{test.participants_contacted}</TableCell>
                          <TableCell>{test.participants_responded}</TableCell>
                          <TableCell>
                            <Badge className={responseRate >= 80 ? "bg-emerald-100 text-emerald-700" : responseRate >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}>
                              {responseRate}%
                            </Badge>
                          </TableCell>
                          <TableCell>{test.average_response_time || "-"}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{test.issues_identified || "None"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Communication Test Dialog */}
      {/* Test Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showTestDialog}
        onOpenChange={(open) => { setShowTestDialog(open); if (!open) setTestStep(0); }}
        title="Log Communication Test"
        currentStep={testStep}
        onStepChange={setTestStep}
        onSubmit={async () => {
          try {
            const testData = {
              test_type: testForm.test_type,
              test_name: `${testForm.test_type.replace('_', ' ')} Test`,
              test_date: new Date().toISOString().split('T')[0],
              participants_contacted: parseInt(testForm.participants_contacted) || 0,
              participants_responded: parseInt(testForm.participants_responded) || 0,
              average_response_time_minutes: testForm.average_response_time ? parseInt(testForm.average_response_time) : null,
              target_response_time_minutes: 30,
              issues_identified: testForm.issues_identified,
              notes: testForm.notes,
            };
            await api.post('/bia-engine/crisis/communication-tests', testData);
            await fetchData();
            setShowTestDialog(false);
            setTestStep(0);
            setTestForm({
              test_type: 'call_tree',
              participants_contacted: 0,
              participants_responded: 0,
              average_response_time: '',
              issues_identified: '',
              notes: '',
            });
            toast.success('Communication test logged successfully');
          } catch (err) {
            toast.error('Failed to log communication test');
          }
        }}
        onCancel={() => setShowTestDialog(false)}
        submitLabel="Log Test"
        canAdvance={true}
        steps={[
          { id: "type", title: "Test Type", subtitle: "What type of communication test did you perform?", isComplete: !!testForm.test_type },
          { id: "results", title: "Results", subtitle: "Record the test results and response metrics.", isComplete: testForm.participants_contacted > 0 },
          { id: "review", title: "Review", subtitle: "Document any issues and additional observations.", isComplete: false },
        ]}
      >
        {/* Step 0: Type */}
        {testStep === 0 && (
          <div className="space-y-4" data-testid="step-type">
            <Label className="text-sm font-semibold">Test Type</Label>
            <div className="space-y-2">
              {[
                { value: "call_tree", label: "Call Tree Test", desc: "Test your escalation call tree" },
                { value: "notification_system", label: "Notification System", desc: "Verify system notifications" },
                { value: "email_broadcast", label: "Email Broadcast", desc: "Test mass email capabilities" },
                { value: "sms_alert", label: "SMS Alert Test", desc: "Verify SMS delivery" },
                { value: "full_drill", label: "Full Communication Drill", desc: "Complete end-to-end drill" },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTestForm(prev => ({ ...prev, test_type: opt.value }))}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    testForm.test_type === opt.value
                      ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`test-type-${opt.value}`}
                >
                  <p className="font-medium text-sm">{opt.label}</p>
                  <p className="text-xs text-slate-500">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Results */}
        {testStep === 1 && (
          <div className="space-y-4" data-testid="step-results">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Participants Contacted</Label>
              <Input
                type="number"
                value={testForm.participants_contacted}
                onChange={(e) => setTestForm(prev => ({ ...prev, participants_contacted: parseInt(e.target.value) || 0 }))}
                data-testid="contacted-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Participants Responded</Label>
              <Input
                type="number"
                value={testForm.participants_responded}
                onChange={(e) => setTestForm(prev => ({ ...prev, participants_responded: parseInt(e.target.value) || 0 }))}
                data-testid="responded-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Average Response Time</Label>
              <Input
                type="text"
                placeholder="e.g., 15 minutes"
                value={testForm.average_response_time}
                onChange={(e) => setTestForm(prev => ({ ...prev, average_response_time: e.target.value }))}
                data-testid="response-time-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Review */}
        {testStep === 2 && (
          <div className="space-y-4" data-testid="step-review">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Issues Identified</Label>
              <Textarea
                placeholder="Any issues or problems encountered during the test"
                value={testForm.issues_identified}
                onChange={(e) => setTestForm(prev => ({ ...prev, issues_identified: e.target.value }))}
                rows={3}
                data-testid="issues-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Notes</Label>
              <Textarea
                placeholder="Additional notes or observations"
                value={testForm.notes}
                onChange={(e) => setTestForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                data-testid="notes-input"
              />
            </div>
          </div>
        )}
      </StepDialog>

      {/* Draft Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showDraftDialog}
        onOpenChange={(open) => { setShowDraftDialog(open); if (!open) { setDraftStep(0); setDraftPreview(null); } }}
        title="Draft Crisis Message"
        currentStep={draftStep}
        onStepChange={setDraftStep}
        onSubmit={handleCreateDraft}
        onCancel={() => { setShowDraftDialog(false); setDraftPreview(null); }}
        submitLabel={saving ? "Generating..." : (draftPreview ? "Create Another" : "Generate Draft")}
        submitDisabled={saving}
        saving={saving}
        canAdvance={
          draftStep === 0 ? !!draftForm.scenario && !!draftForm.audience :
          true
        }
        steps={[
          { id: "scenario", title: "Scenario", subtitle: "What crisis scenario is this communication for and who is the audience?", isComplete: !!draftForm.scenario && !!draftForm.audience },
          { id: "details", title: "Details", subtitle: "Add context notes and review the generated draft.", isComplete: false },
        ]}
      >
        {/* Step 0: Scenario & Audience */}
        {draftStep === 0 && (
          <div className="space-y-4" data-testid="step-scenario">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Crisis Scenario</Label>
              {templates?.scenarios?.map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setDraftForm(prev => ({ ...prev, scenario: s.value }))}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    draftForm.scenario === s.value
                      ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`scenario-${s.value}`}
                >
                  <p className="font-medium text-sm">{s.name}</p>
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Target Audience</Label>
              {templates?.audience_tiers?.map(a => (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setDraftForm(prev => ({ ...prev, audience: a.value }))}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    draftForm.audience === a.value
                      ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`audience-${a.value}`}
                >
                  <p className="font-medium text-sm">{a.name}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Details & Preview */}
        {draftStep === 1 && (
          <div className="space-y-4" data-testid="step-details">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Notes (optional)</Label>
              <Textarea
                placeholder="Add any notes about this communication..."
                value={draftForm.notes}
                onChange={e => setDraftForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                data-testid="notes-input"
              />
            </div>
            {draftPreview && (
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium">Draft Preview</h4>
                <div className="text-sm">
                  <p className="text-slate-500">Subject:</p>
                  <p className="font-medium">{draftPreview.subject}</p>
                </div>
                <div className="text-sm">
                  <p className="text-slate-500">Content:</p>
                  <pre className="whitespace-pre-wrap text-xs bg-white p-3 rounded border max-h-60 overflow-auto">
                    {draftPreview.content}
                  </pre>
                </div>
                {draftPreview.sms_content && (
                  <div className="text-sm">
                    <p className="text-slate-500">SMS:</p>
                    <p className="text-xs bg-white p-2 rounded border">{draftPreview.sms_content}</p>
                  </div>
                )}
                <Badge variant="outline" className="mt-2">
                  Requires approval from: {draftPreview.approval_party}
                </Badge>
              </div>
            )}
          </div>
        )}
      </StepDialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Message Preview</DialogTitle>
            <DialogDescription>
              {selectedMessage?.scenario_name} - {selectedMessage?.audience?.toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <Badge className={STATUS_COLORS[selectedMessage.status]}>
                  {selectedMessage.status?.replace('_', ' ')}
                </Badge>
                <span className="text-sm text-slate-500">
                  Incident: {selectedMessage.incident_id}
                </span>
              </div>
              
              <div className="space-y-2">
                <Label>Subject</Label>
                <p className="p-3 bg-slate-50 rounded-lg font-medium">{selectedMessage.subject}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Content</Label>
                <pre className="p-4 bg-slate-50 rounded-lg text-sm whitespace-pre-wrap max-h-96 overflow-auto">
                  {selectedMessage.content}
                </pre>
              </div>
              
              {selectedMessage.sms_content && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    SMS Version
                  </Label>
                  <p className="p-3 bg-slate-50 rounded-lg text-sm">{selectedMessage.sms_content}</p>
                </div>
              )}
              
              {selectedMessage.approval_history?.length > 0 && (
                <div className="space-y-2">
                  <Label>Approval History</Label>
                  <div className="space-y-2">
                    {selectedMessage.approval_history.map((h, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-sm p-2 bg-slate-50 rounded">
                        {h.action === 'approve' ? <Check className="w-4 h-4 text-emerald-500" /> :
                         h.action === 'reject' ? <X className="w-4 h-4 text-red-500" /> :
                         <Edit className="w-4 h-4 text-amber-500" />}
                        <span>{h.action.toUpperCase()} by {h.user_email}</span>
                        <span className="text-slate-400 ml-auto">{new Date(h.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Close</Button>
            {isAdmin && selectedMessage?.status === 'draft' && (
              <Button onClick={() => { setShowPreviewDialog(false); openApprovalDialog(selectedMessage); }}>
                Review & Approve
              </Button>
            )}
            {isAdmin && selectedMessage?.status === 'approved' && (
              <Button onClick={() => { setShowPreviewDialog(false); handleSend(selectedMessage.id); }}>
                <Send className="w-4 h-4 mr-2" />
                Send Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review & Approve Message</DialogTitle>
            <DialogDescription>
              Emergency Declaration Party authorization required
            </DialogDescription>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">Authority Gate</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      This action will be permanently logged in the Black Box audit system.
                      Only authorized Emergency Declaration Party members may approve crisis communications.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Action</Label>
                <Select value={approvalAction} onValueChange={setApprovalAction}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approve">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-500" />
                        Approve for Sending
                      </div>
                    </SelectItem>
                    <SelectItem value="edit">
                      <div className="flex items-center gap-2">
                        <Edit className="w-4 h-4 text-amber-500" />
                        Edit & Return for Review
                      </div>
                    </SelectItem>
                    <SelectItem value="reject">
                      <div className="flex items-center gap-2">
                        <X className="w-4 h-4 text-red-500" />
                        Reject
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {approvalAction === 'edit' && (
                <div className="space-y-2">
                  <Label>Edit Content</Label>
                  <Textarea
                    value={editedContent}
                    onChange={e => setEditedContent(e.target.value)}
                    rows={10}
                    className="font-mono text-sm"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Add approval notes..."
                  value={approvalNotes}
                  onChange={e => setApprovalNotes(e.target.value)}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleApproval} 
              disabled={saving}
              variant={approvalAction === 'reject' ? 'destructive' : 'default'}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {approvalAction === 'approve' ? 'Approve' : approvalAction === 'edit' ? 'Save Edit' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
